import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef} from '@angular/core';
import {BehaviorSubject, filter, skip, Subscription, take} from 'rxjs';
import {
  SortTable,
  SortTableColumn,
  SortTablePagination,
  SortTableRow,
  SortTableSortDirection,
  SortTableUniqueRowId,
} from '../../../shared/models/sort-table/types';
import {BulkSelectCapabilities} from '../../../shared/models/bulk-select';
import {TILES_EXISTING_COL_IDX_KEBAB} from 'src/shared/constants/tiles-existing-table-indexes';
import {NgClass, NgStyle} from '@angular/common';
import {RoundCheckboxComponent} from '../round-checkbox/round-checkbox.component';
import {PaginatorComponent} from '../paginator/paginator.component';
import {BulkActionToolComponent} from '../bulk-action-tool/bulk-action-tool.component';
import {SortTableRowComponent} from './row/sort-table-row.component';
import {SortTableInstanceService} from 'src/core/services/sort-table-instance.service';
import {ShadeToolsService} from 'src/core/services/shade-tools.service';

@Component({
  selector: 'motn-sort-table',
  templateUrl: './sort-table.component.html',
  styleUrls: ['./sort-table.component.scss'],
  providers: [SortTableInstanceService],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgClass,
    NgStyle,
    RoundCheckboxComponent,
    PaginatorComponent,
    BulkActionToolComponent,
    SortTableRowComponent,
  ],
})
export class SortTableComponent {
  @Input() bulkSelectCapabilities: BulkSelectCapabilities;
  @Input() paginationManagedByParent: boolean = false;
  @Input() autoScroll: boolean = true;
  @Input() disableUrlUpdate: boolean = false;
  @Input() table$: BehaviorSubject<SortTable>;
  /**
   * This class handles things like search filters and pagination, so the parent
   * component might not know (easily) which rows are rendered at any given time.
   * That's what this @Output makes happen (easily).
   */
  @Output() renderedRows$: EventEmitter<SortTableRow[]> = new EventEmitter<SortTableRow[]>();
  @Output() pageNumberChanged$: EventEmitter<number> = new EventEmitter<number>();

  private table$Sub: Subscription;

  table: SortTable;
  currentPageRows: SortTableRow[];
  columns: SortTableColumn[];
  pagination: SortTablePagination = { enabled: false };
  private _elementsPerPage: number = 10;
  private _maxPageOptions: number = 5;

  private lastUpdateTime?: number;
  private isInitializing: boolean = true;
  private consecutiveQuickUpdates = 0;
  private readonly MAX_CONSECUTIVE_QUICK_UPDATES = 10;

  currentPageNumber: number = 1;

  sortedColumnIndex: number | undefined;
  sortedColumnDirection: SortTableSortDirection;

  bulkSelectActive: boolean;
  private bulkSelectActiveSub: Subscription;
  bulkSelectedValues: SortTableUniqueRowId[] = [];
  private bulkSelectedValuesSub: Subscription;


  /**
   * Tracks whether we ran sortByDefaultOnInit, which should only have an effect
   * once. We use this variable to ensure it only works once.
   *
   * @private
   * @see sortByDefaultOnInit
   */
  private alreadySortedByDefault: boolean = false;


  constructor(
    private shadeTools: ShadeToolsService,
    private tableService: SortTableInstanceService,
    private cdr: ChangeDetectorRef,
  ) {
  }


  ngOnInit(): void {
    this.table$.pipe(
      filter(st => !!st),
      take(1),
    ).subscribe(
      (st) => {
        this.table = st;
        this.columns = st.columns;
        this.pagination = st.pagination;
        // Update cached values
        if (st.pagination?.enabled) {
          this._elementsPerPage = st.pagination.rowsPerPage || 10;
          this._maxPageOptions = st.pagination.maxPageOptions || 5;
        } else {
          this._elementsPerPage = 10;
          this._maxPageOptions = 5;
        }
        // Always reset to page 1 for new table data to prevent URL interference
        this.currentPageNumber = 1;

        this.sortByDefaultOnInit();
        this.cdr.detectChanges();
        // Mark initialization complete after a short delay to allow first render
        setTimeout(() => {
          this.isInitializing = false;
        }, 100);
      },
    );

    this.table$Sub = this.table$.pipe(
      filter(st => !!st),
      skip(1),
    ).subscribe(
      (st) => {
        this.table = st;
        this.columns = st.columns;
        this.pagination = st.pagination;
        // Update cached values
        if (st.pagination?.enabled) {
          this._elementsPerPage = st.pagination.rowsPerPage || 10;
          this._maxPageOptions = st.pagination.maxPageOptions || 5;
        } else {
          this._elementsPerPage = 10;
          this._maxPageOptions = 5;
        }
        // this.currentPageNumber = st.pagination?.startOnPage;

        this.renderRows();
        this.cdr.detectChanges();
      },
    );

    this.bulkSelectActiveSub = this.tableService.bulkSelectActive$.subscribe({
      next: (active) => {
        this.bulkSelectActive = active;
      },
    });

    this.bulkSelectedValuesSub = this.tableService.bulkSelectedRowsIds$.subscribe({
      next: (values) => {
        this.bulkSelectedValues = values;
      },
    });
  }


  ngOnDestroy() {
    this.table$Sub?.unsubscribe();
    this.bulkSelectActiveSub?.unsubscribe();
    this.bulkSelectedValuesSub?.unsubscribe();
  }


  get totalRowCount(): number {
    if (this.paginationManagedByParent && this.pagination?.totalRowQty) {
      return this.pagination.totalRowQty;
    }

    return this.table?.rows?.length || 0;
  }

  get elementsPerPage(): number {
    return this._elementsPerPage;
  }

  get maxPageOptions(): number {
    return this._maxPageOptions;
  }


  /**
   *
   * @private
   */
  private sortByDefaultOnInit() {
    if (this.alreadySortedByDefault) {
      this.renderRows();
      return;
    }

    this.alreadySortedByDefault = true;

    for (let i in this.columns) {
      if (this.columns[i].sortDefault) {
        // Set sort state directly without triggering page changes during initialization
        this.sortedColumnIndex = +i;
        this.sortedColumnDirection = this.columns[i].sortDefault;
        this.currentPageNumber = 1;
        this.renderRows();
        return;
      }
    }

    this.renderRows();
  }


  private renderRows() {
    if (this.paginationManagedByParent) {
      this._renderRowsServerSorted();
    } else {
      this._renderRowsClientSorted();
    }
  }


  private _renderRowsServerSorted() {
    this.currentPageRows = [...this.table.rows];
    this.renderedRows$.next(this.currentPageRows);
  }


  private _renderRowsClientSorted() {
    let rows = [...this.table.rows];

    if (this.sortedColumnIndex >= 0) {
      rows = this.columns[this.sortedColumnIndex].sortingFn(this.sortedColumnIndex, rows);
    }

    if (this.sortedColumnDirection === 'down') {
      rows.reverse();
    }

    if (this.pagination?.enabled) {
      const currentPageIndex = this.currentPageNumber - 1;
      rows = rows.slice(currentPageIndex * this.pagination.rowsPerPage, this.currentPageNumber * this.pagination.rowsPerPage);
    }

    // Always update if we detect frequent table updates (bulk processing mode)
    const now = Date.now();
    const timeSinceLastUpdate = now - (this.lastUpdateTime || 0);
    this.lastUpdateTime = now;

    // Track consecutive quick updates to prevent infinite loops
    if (timeSinceLastUpdate < 1000) {
      this.consecutiveQuickUpdates++;
    } else {
      this.consecutiveQuickUpdates = 0;
    }

    // If updates are coming frequently (less than 1 second apart), force updates
    // But only if we actually have rows to process and haven't exceeded the safety limit
    const isBulkProcessing = timeSinceLastUpdate < 1000 &&
                            rows.length > 0 &&
                            this.consecutiveQuickUpdates <= this.MAX_CONSECUTIVE_QUICK_UPDATES;
    const contextsAreDifferent = this.checkRowContextsAreDifferent(this.currentPageRows, rows);
    const isInitialRender = !this.currentPageRows || this.currentPageRows.length === 0;

    if (isBulkProcessing || contextsAreDifferent || isInitialRender) {
      this.currentPageRows = rows;
      this.renderedRows$.next(rows);
    }
  }


  /**
   * Given two arrays of rows, this function tells you if the contexts
   * from one array are not exactly the same as and in the exact same
   * order as the contexts from the second array.
   *
   * @param oldRows
   * @param newRows
   * @private
   */
  private checkRowContextsAreDifferent(oldRows: SortTableRow[], newRows: SortTableRow[]): boolean {
    if (oldRows?.length !== newRows.length) {
      return true;
    }

    for (let i = 0; i < oldRows.length; i++) {
      if (oldRows[i].context !== newRows[i].context) {
        return true;
      }
    }

    return false;
  }


  infoDialog(text: string) {
    this.shadeTools.infoDialog(text);
  }


  cancelBulkSelect() {
    this.tableService.deactivateBulkSelect();
  }


  /**
   * Applies sorting to given column. If sorting was already enabled, applies
   * the next sorting direction to the same column. The sequence of directions
   * is:
   *    1. 'up'
   *    2. 'down'
   *    3. undefined
   *
   * @param columnIndex
   */
  sortColumn(columnIndex: number) {
    if (this.sortedColumnIndex !== columnIndex) {
      this.sortedColumnDirection = undefined;
    }

    if (this.sortedColumnDirection === undefined) {
      this.sort(columnIndex, 'up');
    } else if (this.sortedColumnDirection === 'up') {
      this.sort(columnIndex, 'down');
    } else {
      this.sort(undefined, undefined);
    }
  }


  sort(columnIndex: number, direction: SortTableSortDirection) {
    this.sortedColumnIndex = columnIndex;
    this.sortedColumnDirection = direction;
    // Set page number directly without emitting event during sort to prevent URL feedback loop
    this.currentPageNumber = 1;
    this.renderRows();
  }


  changePageNumber(pageNumber: number) {
    // Prevent external page changes during initialization to avoid URL/paginator interference
    if (this.isInitializing) {
      return;
    }
    this.currentPageNumber = pageNumber;
    this.pageNumberChanged$.emit(pageNumber);
    this.renderRows();
  }


  rowClicked(row: SortTableRow) {
    if (row.clickCallback) {
      row.clickCallback();
    }
  }


  rowLoaded(row: SortTableRow) {
    if (row.onInitCallback) {
      row.onInitCallback();
    }
  }


  get allPageRowsAreSelected(): boolean {
    return this.currentPageRows.every(row => this.tableService.isRowSelected(row));
  }


  toggleAllRows(): void {
    if (this.allPageRowsAreSelected) {
      this.currentPageRows.forEach(row => this.tableService.deselectRow(row));
    } else {
      this.currentPageRows.forEach(row => this.tableService.selectRow(row));
    }

    // doing this to trigger this._bulkSelected update for rows in sort-table-cell-kebab-menu.compo nent.ts as it has sub to bulkSelectActive$
    this.tableService.activateBulkSelect();
  }


  isKebabColumn(colIndex: number): boolean {
    return TILES_EXISTING_COL_IDX_KEBAB === colIndex;
  }
}
